/* rn-personalise.js (W3, website remake, 29 Oct 2026).
   Passive enhancer for snippets/rn-personalise.liquid. Rules (PLAN.md): never listens to or cancels
   submit, never touches the add button, cart, drawer or checkout. It only:
   1. shrinks very large photos and turns HEIC into JPEG when this browser can decode it,
      then swaps input.files (DataTransfer); on any failure the original file stays;
   2. shows a small preview;
   3. "I'll send photos after": hides and disables the photo inputs, relaxes required wording;
   4. variant-conditional boxes: shown, enabled and required only for the trigger option.
   Validation is native (required, maxlength, type=email, setCustomValidity for oversize files). */
(() => {
  if (window.rnPersonalise) return;
  window.rnPersonalise = true;

  const MAX_BYTES = 15 * 1024 * 1024; // live limit, proven on orders (Shopify's own ceiling not confirmed)
  const SHRINK_OVER_BYTES = 8 * 1024 * 1024; // a 12MP iPhone JPEG (~3-6 MB) is sent untouched
  const MAX_SIDE = 4032; // 12MP long side; also under iOS canvas area limits (4032 x 3024 = 12.2M px)
  const QUALITY = 0.9;

  const isHeic = (file) => /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name || '');
  const jpegName = (name) => (String(name || 'photo').replace(/\.[^./\\]+$/, '') || 'photo') + '.jpg';

  const decode = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const timer = setTimeout(() => { URL.revokeObjectURL(url); reject(new Error('timeout')); }, 15000);
    img.onload = () => { clearTimeout(timer); resolve({ img, url }); };
    img.onerror = () => { clearTimeout(timer); URL.revokeObjectURL(url); reject(new Error('decode')); };
    img.src = url; // browsers apply EXIF orientation when drawing an <img> (image-orientation: from-image)
  });

  // Returns a smaller/compatible File, or null to keep the original.
  const prepare = async (file) => {
    const heic = isHeic(file);
    if (!heic && file.size <= SHRINK_OVER_BYTES) return null;
    if (!/^image\//i.test(file.type) && !heic) return null;
    let decoded;
    try { decoded = await decode(file); } catch (_) { return null; } // e.g. HEIC on Chrome: send as is
    const { img, url } = decoded;
    try {
      const w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h) return null;
      const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', QUALITY));
      canvas.width = canvas.height = 0; // free memory on iOS
      if (!blob || blob.size < 1024) return null;
      if (!heic && blob.size >= file.size) return null;
      return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified || Date.now() });
    } catch (_) {
      return null;
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const swapFile = (input, file) => {
    try {
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files; // does not fire change
      return input.files.length === 1;
    } catch (_) {
      return false;
    }
  };

  const showPreview = (input) => {
    const upload = input.closest('[data-rn-upload]');
    if (!upload) return;
    const box = upload.querySelector('[data-rn-preview]');
    const img = upload.querySelector('[data-rn-preview-img]');
    const text = upload.querySelector('[data-rn-preview-text]');
    if (input.dataset.rnUrl) { URL.revokeObjectURL(input.dataset.rnUrl); delete input.dataset.rnUrl; }
    const file = input.files && input.files[0];
    upload.classList.toggle('rn-p__upload--ready', Boolean(file));
    if (!box) return;
    if (!file) { box.hidden = true; if (img) { img.hidden = true; img.removeAttribute('src'); } return; }
    box.hidden = false;
    if (text) text.textContent = 'Photo added';
    if (!img) return;
    const url = URL.createObjectURL(file);
    input.dataset.rnUrl = url;
    img.onload = () => { img.hidden = false; };
    img.onerror = () => { img.hidden = true; }; // e.g. HEIC on Chrome: text only, file still sent
    img.src = url;
  };

  const checkSize = (input) => {
    const file = input.files && input.files[0];
    const big = file && file.size > MAX_BYTES;
    input.setCustomValidity(big ? 'This photo is over 15 MB. Please choose a smaller one, or tick "I\'ll send photos after ordering".' : '');
    return !big;
  };

  // 1 Nov (swarm fix batch 4, B1): while a photo is being got ready (up to 15 s for a big or HEIC photo) the field is marked not
  // ready, so Add can't send the unchecked original; checkSize() replaces this once the job ends.
  const PENDING = 'Your photo is still getting ready. Please wait a moment, then press Add again.';
  const onPhoto = async (input) => {
    const file = input.files && input.files[0];
    const token = String(Math.random());
    input.dataset.rnJob = token; // 1 Nov (batch 4, T3): any change, even a clear, makes an in-flight restore stand down
    if (!file) {
      input.setCustomValidity('');
      showPreview(input);
      const root = input.closest('[data-rn-p]');
      if (root) savePhoto(root, input);
      return;
    }
    input.setCustomValidity(PENDING);
    const upload = input.closest('[data-rn-upload]');
    const text = upload && upload.querySelector('[data-rn-preview-text]');
    const box = upload && upload.querySelector('[data-rn-preview]');
    if (box && text && (isHeic(file) || file.size > SHRINK_OVER_BYTES)) { box.hidden = false; text.textContent = 'Getting your photo ready…'; }
    let next = null;
    try { next = await prepare(file); } catch (_) { next = null; }
    if (input.dataset.rnJob !== token || input.files[0] !== file) return; // buyer picked another file meanwhile
    if (next) swapFile(input, next);
    checkSize(input);
    showPreview(input);
    const root = input.closest('[data-rn-p]');
    if (root) savePhoto(root, input);
  };

  /* 31 Oct (swarm fix 3): keep the buyer's details if they leave and come back before adding.
     Shopify sometimes sends product pages with Cache-Control: no-store, so Chrome rebuilds the page on Back: typed
     text comes back, a chosen photo never does. The chosen photo (a File) is kept in this browser's IndexedDB and the
     typed wording in sessionStorage, per product page, then put back into the same inputs, so the normal add sends
     them. Both are dropped once that product is added, when the photo is cleared, and after a day. */
  const DAY = 24 * 60 * 60 * 1000;
  const pageKey = (root) => `${location.pathname}|${root.dataset.rnFormId || ''}`;
  const textKey = (root) => `rn-p-text:${pageKey(root)}`;
  const ss = {
    get(k) { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch (_) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (_) {} },
    del(k) { try { sessionStorage.removeItem(k); } catch (_) {} }
  };
  /* 1 Nov (swarm fix batch 4, T2): IndexedDB is shared by every tab, sessionStorage is this tab's own. Each tab gets its own
     draft id (kept in sessionStorage) and its photos are saved under that id, so a new tab never picks up a photo another
     tab chose, and two tabs on the same product never overwrite each other. */
  const draftId = () => {
    let id = ss.get('rn-p-draft');
    if (!id) { id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`; ss.set('rn-p-draft', id); }
    return id;
  };
  const photoKey = (root, input) => `${pageKey(root)}|${input.name}|${draftId()}`;
  let dbPromise = null;
  const db = () => {
    if (!dbPromise) {
      dbPromise = new Promise((resolve) => {
        try {
          const req = indexedDB.open('rn-personalise', 1);
          req.onupgradeneeded = () => req.result.createObjectStore('photos');
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => resolve(null);
          req.onblocked = () => resolve(null);
        } catch (_) { resolve(null); }
      });
    }
    return dbPromise;
  };
  const idb = async (mode, fn) => {
    const d = await db();
    if (!d) return undefined;
    return new Promise((resolve) => {
      try {
        const tx = d.transaction('photos', mode);
        const req = fn(tx.objectStore('photos'));
        tx.oncomplete = () => resolve(req && req.result);
        tx.onerror = tx.onabort = () => resolve(undefined);
      } catch (_) { resolve(undefined); }
    });
  };
  const savePhoto = (root, input) => {
    const file = input.files && input.files[0];
    const key = photoKey(root, input);
    if (!file) { idb('readwrite', (s) => s.delete(key)); ss.del(`rn-p-photo:${key}`); return; }
    ss.set(`rn-p-photo:${key}`, file.name || 'photo');
    idb('readwrite', (s) => s.put({ file, at: Date.now() }, key));
  };
  const saveText = (root) => {
    const values = {};
    root.querySelectorAll('textarea[name^="properties["], input[name^="properties["]:not([type="file"])').forEach((el) => {
      values[el.name] = el.type === 'checkbox' ? el.checked : el.value;
    });
    ss.set(textKey(root), values);
  };
  const forget = (root) => {
    ss.del(textKey(root));
    fileInputs(root).forEach((input) => {
      const key = photoKey(root, input);
      ss.del(`rn-p-photo:${key}`);
      idb('readwrite', (s) => s.delete(key));
    });
  };
  const restore = async (root) => {
    const values = ss.get(textKey(root));
    if (values) {
      root.querySelectorAll('textarea[name^="properties["], input[name^="properties["]:not([type="file"])').forEach((el) => {
        if (!(el.name in values)) return;
        if (el.type === 'checkbox') { if (values[el.name] && !el.checked) el.checked = true; }
        else if (!el.value && values[el.name]) el.value = values[el.name];
      });
      sync(root);
    }
    for (const input of fileInputs(root)) {
      if (input.files && input.files.length) continue; // the browser kept it itself (back/forward cache)
      const key = photoKey(root, input);
      const chosen = ss.get(`rn-p-photo:${key}`);
      if (!chosen) continue; // 1 Nov (batch 4, T2): this tab never chose a photo here, so nothing of this tab's to put back
      const gen = input.dataset.rnJob || '';
      const saved = await idb('readonly', (s) => s.get(key));
      // 1 Nov (batch 4, T3): the buyer picked (or cleared) a photo while the saved one was being read: theirs wins.
      if ((input.files && input.files.length) || (input.dataset.rnJob || '') !== gen) continue;
      if (saved && saved.file && Date.now() - (saved.at || 0) < DAY && swapFile(input, saved.file)) {
        checkSize(input);
        showPreview(input);
        const text = input.closest('[data-rn-upload]')?.querySelector('[data-rn-preview-text]');
        if (text) text.textContent = 'Photo added (kept from before)';
      } else if (chosen) {
        const upload = input.closest('[data-rn-upload]');
        const box = upload && upload.querySelector('[data-rn-preview]');
        const text = upload && upload.querySelector('[data-rn-preview-text]');
        if (box && text) { box.hidden = false; text.textContent = 'Please choose your photo again.'; }
        if (saved) idb('readwrite', (s) => s.delete(key));
      }
    }
  };
  // Old photos never linger: anything over a day old is removed on any product page visit.
  const sweep = () => idb('readwrite', (s) => {
    const req = s.openCursor();
    req.onsuccess = () => {
      const c = req.result;
      if (!c) return;
      if (!c.value || Date.now() - (c.value.at || 0) > DAY) c.delete();
      c.continue();
    };
    return req;
  });

  const fileInputs = (root) => Array.from(root.querySelectorAll('[data-rn-photo]'));
  const textInputs = (root) => Array.from(root.querySelectorAll('textarea[data-rn-required], input[data-rn-required]:not([type="file"])'));
  const isActive = (root) => root.dataset.rnActive !== 'false';

  const sync = (root) => {
    const active = isActive(root);
    const later = root.querySelector('[data-rn-later]');
    const deferred = Boolean(later && later.checked);
    const photos = root.querySelector('[data-rn-photos]');
    const help = root.querySelector('[data-rn-later-help]');
    if (photos) photos.hidden = deferred;
    if (help) help.hidden = !deferred;
    if (later) later.disabled = !active;
    fileInputs(root).forEach((input) => {
      input.disabled = !active || deferred;
      input.required = active && !deferred && input.hasAttribute('data-rn-required');
    });
    textInputs(root).forEach((input) => {
      input.disabled = !active;
      input.required = active && !deferred;
      const status = input.closest('[data-rn-field]')?.querySelector('[data-rn-status]');
      if (status) status.textContent = deferred ? 'Optional for now' : 'Required';
    });
    root.hidden = !active;
  };

  const triggerValues = (root) => (root.dataset.rnTriggerValues || '')
    .split('||').map((v) => v.trim().toLowerCase()).filter(Boolean);

  const onVariant = (variant) => {
    if (!variant) return;
    document.querySelectorAll('[data-rn-p].rn-p--cond').forEach((root) => {
      const index = Number.parseInt(root.dataset.rnTriggerIndex || '', 10);
      if (!Number.isInteger(index) || index < 0) return;
      const value = String((variant.options && variant.options[index]) ?? variant[`option${index + 1}`] ?? '').trim().toLowerCase();
      root.dataset.rnActive = triggerValues(root).includes(value) ? 'true' : 'false';
      sync(root);
    });
  };

  // 1 Nov (swarm fix batch 3, fix 3): the wording box had a hidden character limit (maxlength 256 on the paw marker).
  // The line under each box says the limit, then counts as the buyer types (and after a restore).
  const count = (root) => {
    root.querySelectorAll("[data-rn-count]").forEach((line) => {
      const box = document.getElementById(line.dataset.rnCount);
      if (!box || !(box.maxLength > 0)) return;
      const used = box.value.length;
      line.textContent = used ? `${used} / ${box.maxLength} characters` : `Up to ${box.maxLength} characters`;
      line.classList.toggle("rn-p__count--full", used >= box.maxLength);
    });
  };

  const init = (root) => {
    if (root.dataset.rnReady === 'true') return;
    root.dataset.rnReady = 'true';
    root.classList.add('rn-p--js');
    root.addEventListener('change', (event) => {
      const t = event.target;
      if (t.matches('[data-rn-photo]')) onPhoto(t);
      else if (t.matches('[data-rn-later]')) { sync(root); saveText(root); }
    });
    root.addEventListener('input', (event) => {
      if (event.target.matches('textarea[name^="properties["], input[name^="properties["]:not([type="file"])')) saveText(root);
      if (event.target.matches('textarea')) count(root); // 1 Nov (swarm fix batch 3, fix 3)
    });
    // Links in the form (privacy policy) open in a new tab, so the buyer's details stay on this page.
    root.querySelectorAll('a[href]:not([href^="mailto:"])').forEach((a) => {
      a.target = '_blank';
      a.rel = 'noopener';
    });
    sync(root);
    fileInputs(root).forEach((input) => { if (input.files && input.files.length) onPhoto(input); }); // bfcache / restored form
    // The browser puts typed text back just after load; restore what it didn't.
    setTimeout(() => restore(root), 50);
    count(root); // 1 Nov (swarm fix batch 3, fix 3): again once the browser / restore has put text back
    setTimeout(() => count(root), 400);
    window.addEventListener('pageshow', () => count(root));
  };

  const initAll = () => document.querySelectorAll('[data-rn-p]').forEach(init);
  document.addEventListener('product-ajax:added', (event) => {
    const added = event.detail && event.detail.product;
    const handle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!added || !handle || (added.handle && added.handle !== handle)) return;
    document.querySelectorAll('[data-rn-p]').forEach(forget);
  });
  setTimeout(sweep, 3000);
  // Stock product-info.js dispatches this on document after a variant change (read only).
  document.addEventListener('variant:changed', (event) => onVariant(event.detail && event.detail.variant));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
